import React, { useState, useCallback, useEffect } from "react";
import Header from "./components/Header";
import AssumptionsForm from "./components/AssumptionsForm";
import JourneyTable from "./components/JourneyTable";
import MilestonesTable from "./components/MilestonesTable";
import { PhaseData, Assumptions } from "./types";
import { calculateGoalTotalAssets } from "./utils/calculations";
import "./App.css";

const App: React.FC = () => {
  const [assumptions, setAssumptions] = useState<Assumptions>({
    annualIncome: 120000,
    planMTI: 9,
    maxDTA: 40,
    minDSCR: 3.5,
    resMonths: 12,
  });

  const [recalcTrigger, setRecalcTrigger] = useState(0);

  const [defaultAssetPercents, setDefaultAssetPercents] = useState<number[]>([
    0.1, 0.5, 0.9, 1.0,
  ]);

  const [phaseData, setPhaseData] = useState<PhaseData[]>(() => {
    return Array.from({ length: 4 }, () => ({
      dta: 0.4,
      dscr: 3.5,
      mti: 0.09,
      resMonths: 12,
      mty: 0.12,
    }));
  });

  const goalTotalAssets = calculateGoalTotalAssets(
    assumptions.annualIncome,
    assumptions.maxDTA / 100,
    assumptions.planMTI / 100,
    assumptions.minDSCR
  );

  const initializePhaseData = useCallback(() => {
    const dtaMax = assumptions.maxDTA / 100;
    const dscrMin = assumptions.minDSCR;
    const planMTI = assumptions.planMTI / 100;
    const reserves = assumptions.resMonths;

    setPhaseData((prev) =>
      prev.map(() => ({
        dta: dtaMax,
        dscr: dscrMin,
        mti: planMTI,
        resMonths: reserves,
        mty: dtaMax * dscrMin * planMTI,
      }))
    );
  }, [assumptions]);

  const handleRecalculate = () => {
    // Reset phase data to match the new assumptions
    initializePhaseData();
    // Trigger recalculation
    setRecalcTrigger((prev) => prev + 1);
  };

  const handlePhaseDataChange = (
    index: number,
    field: keyof PhaseData,
    value: number
  ) => {
    setPhaseData((prev) => {
      const newData = [...prev];
      newData[index] = { ...newData[index], [field]: value };
      return newData;
    });
  };

  const handleAssetPercentChange = (index: number, value: number) => {
    setDefaultAssetPercents((prev) => {
      const newPercents = [...prev];
      newPercents[index] = value;
      return newPercents;
    });
  };

  // Trigger recalculation when phase data changes
  useEffect(() => {
    setRecalcTrigger((prev) => prev + 1);
  }, [phaseData]);

  const handleAssumptionsChange = (newAssumptions: Assumptions) => {
    setAssumptions(newAssumptions);

    // Auto-update phase data when assumptions change
    const dtaMax = newAssumptions.maxDTA / 100;
    const dscrMin = newAssumptions.minDSCR;
    const planMTI = newAssumptions.planMTI / 100;
    const reserves = newAssumptions.resMonths;

    setPhaseData((prev) =>
      prev.map((phase, index) => ({
        dta: dtaMax,
        dscr: dscrMin,
        mti: planMTI,
        resMonths: reserves,
        mty: dtaMax * dscrMin * planMTI,
      }))
    );

    // The useEffect will trigger recalculation automatically
  };

  return (
    <div className="app">
      <div className="container">
        <Header />
        <div className="content">
          <AssumptionsForm
            assumptions={assumptions}
            goalTotalAssets={goalTotalAssets}
            onAssumptionsChange={handleAssumptionsChange}
            onRecalculate={handleRecalculate}
          />

          <JourneyTable
            phaseData={phaseData}
            defaultAssetPercents={defaultAssetPercents}
            goalTotalAssets={goalTotalAssets}
            onPhaseDataChange={handlePhaseDataChange}
            onAssetPercentChange={handleAssetPercentChange}
            key={recalcTrigger}
          />

          <MilestonesTable
            income={assumptions.annualIncome}
            dtaMax={assumptions.maxDTA / 100}
            planMTI={assumptions.planMTI / 100}
            phaseData={phaseData}
          />
        </div>
      </div>
    </div>
  );
};

export default App;
